import { TextField } from '@nike/eds'
import { VirtualKeyboard } from 'components/keyboard'
import { TrinityButton } from 'components/trinity-button'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type CountryOfOrigin } from 'types'
import { isEmea } from 'utils/dcContext'

import { CountrySelect } from './country-select'

interface ManualEntryProps {
  styleColor: string
  size: string
  onStyleColorChange: (value: string) => void
  onSizeChange: (value: string) => void
  styleColorRef: React.RefObject<HTMLInputElement>
  confirm: (country: CountryOfOrigin | undefined) => void
  isLoading: boolean
}

export const ManualEntry = ({
  styleColor,
  size,
  onStyleColorChange,
  onSizeChange,
  styleColorRef,
  confirm,
  isLoading
}: ManualEntryProps) => {
  const { t } = useTranslation()

  const sizeRef = useRef<HTMLInputElement>(null)
  const [country, setCountry] = useState<CountryOfOrigin | undefined>(undefined)
  const [cooIsOpen, setCooIsOpen] = useState(false)
  const [styleError, setStyleError] = useState('')
  const [sizeError, setSizeError] = useState('')
  const [cooError, setCooError] = useState('')
  const [focusedTextField, setFocusedTextField] = useState('styleColor')

  // React is asynchronous in nature, small timeout trick to ensure rerender happens after state update
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      styleColorRef.current?.focus()
    }, 0)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  useEffect(() => {
    setCooError('')
  }, [country])

  const updateStyleColor = (value: string) => {
    const DASH_POSITION = 6
    const cleanValue = value.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase()
    const firstPart = cleanValue.slice(0, DASH_POSITION).replace(/-/g, '')
    if (cleanValue.length <= DASH_POSITION) {
      onStyleColorChange(firstPart)
      return
    }
    let secondPart = cleanValue[DASH_POSITION] === '-'
      ? cleanValue.slice(DASH_POSITION + 1)
      : cleanValue.slice(DASH_POSITION)
    secondPart = secondPart.replace(/-/g, '').slice(0, 3)
    const formattedValue = `${firstPart}-${secondPart}`
    onStyleColorChange(formattedValue)
    if (firstPart.length === 6 && secondPart.length === 3) {
      sizeRef.current?.focus()
    }
  }

  const sizeTabToCoOHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      setCooIsOpen(true)
    }
  }

  const submitHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      confirmManualEntry()
    }
  }

  const confirmManualEntry = () => {
    let hasErrors = false
    if (size.length === 0) {
      setSizeError(t('scan_unit.manual_entry.size_error'))
      hasErrors = true
    }
    if (styleColor.length === 0) {
      setStyleError(t('scan_unit.manual_entry.style_color_required_error'))
      hasErrors = true
    } else if (styleColor.length < 10) {
      setStyleError(t('scan_unit.manual_entry.style_color_length_error'))
      hasErrors = true
    }
    if (isEmea && !country) {
      setCooError(t('scan_unit.manual_entry.coo_error'))
      hasErrors = true
    }
    if (hasErrors) return
    confirm(country)
  }

  const virtualKeyboardText = () => {
    if (focusedTextField === 'styleColor') {
      return styleColor
    } else if (focusedTextField === 'size') {
      return size
    }
    return ''
  }

  const handleVirtualKeyboardChange = (kbText: string) => {
    if (focusedTextField === 'styleColor') {
      setStyleError('')
      updateStyleColor(kbText)
    } else if (focusedTextField === 'size') {
      setSizeError('')
      onSizeChange(kbText)
    }
  }

  return (
    <>
      <div className="flex flex-col bottom-0" onKeyDown={submitHandler}>
          <h1 className="text-6xl font-medium mb-4 mx-auto">{t('scan_unit.manual_entry.title')}</h1>
          <hr />
          <div className="flex gap-12">
              <div className={isEmea ? 'w-1/2' : 'w-full'}>
                  <TextField
                      id="style-color"
                      label=""
                      autoComplete='off'
                      placeholder={t('scan_unit.manual_entry.style_color_placeholder')}
                      value={styleColor}
                      hasErrors={!!styleError}
                      errorMessage=' '
                      onChange={e => {
                        setStyleError('')
                        updateStyleColor(e.currentTarget.value)
                      }}
                      ref={styleColorRef}
                      maxChars={10}
                      onFocus={() => { setFocusedTextField('styleColor') }}
                  />
                  <p style={{ marginTop: '-22px' }} className={`text-s ${styleError ? 'text-[--eds-color-text-danger]' : 'text-gray-500'}`}>{styleError || t('scan_unit.manual_entry.style_color_label')}</p>
                  <TextField
                      id="size"
                      label=""
                      autoComplete='off'
                      placeholder={t('scan_unit.manual_entry.size_placeholder')}
                      value={size}
                      hasErrors={!!sizeError}
                      errorMessage=' '
                      onChange={e => {
                        setSizeError('')
                        onSizeChange(e.currentTarget.value.toUpperCase())
                      }}
                      onKeyDown={sizeTabToCoOHandler}
                      ref={sizeRef}
                      onFocus={() => { setFocusedTextField('size') }}
                  />
                  <p style={{ marginTop: sizeError ? '0px' : '4px' }} className={`text-s ${sizeError ? 'text-[--eds-color-text-danger]' : 'text-gray-500'} `}>{sizeError || t('scan_unit.manual_entry.size_label')}</p>
              </div>

              {isEmea && (
                  <div className="w-1/2 mt-10">
                      <CountrySelect
                          isOpen={cooIsOpen}
                          setIsOpen={setCooIsOpen}
                          selected={country}
                          onChange={setCountry}
                          confirm={confirmManualEntry}
                      />
                      <p className={`text-s ${cooError ? 'text-[--eds-color-text-danger]' : 'text-gray-500'}`}>{cooError || t('scan_unit.manual_entry.coo_label')}</p>
                  </div>
              )}
          </div>
          <TrinityButton
              className={'mt-8 w-full'}
              onClick={confirmManualEntry}
              loading={isLoading}
              disabled={isLoading}
              disabledMessage={t('loading')}
          >
              <div className={'flex items-center'}>
                  <img src='/images/enter-key-icon.png' className={'h-14 pr-2'} alt={'Enter keyboard button image'}/>
                  {t('buttons.confirm')}
              </div>
          </TrinityButton>
      </div>
      <div className='absolute bottom-0 w-[900px]'>
        <VirtualKeyboard
          text={virtualKeyboardText()}
          setText={handleVirtualKeyboardChange}
          whichKeyboard={'alphanumeric'}
          heightIconDiv={'200px'}
          canCloseKeyboard={true}
          confirm={confirmManualEntry}
          showPunctuationMarks={true}
        />
      </div>
    </>
  )
}
